/**
 * ClashApprovalDialog — Stitch design
 * Getoond bij het verlengen (+1 / -1 dag) van een draaidag-reservering wanneer
 * materiaal op de extra dag al door een ANDERE draaidag gereserveerd is.
 * Per clashend materiaal kan de gebruiker de dubbele boeking GOEDKEUREN of
 * het materiaal VERVANGEN door een vrij materiaal uit dezelfde categorie.
 */
import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogActions, Box, Typography, TextField } from '@mui/material';

const T = {
  primary: '#526075', primaryDim: '#465469', primaryContainer: '#d5e3fd', tertiary: '#635983',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  error: '#a83836', errorBg: '#fef2f2',
  amber: '#f59e0b', amberBg: '#fef3c7',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

const fmtDate = (d) => {
  if (!d) return '';
  const date = d instanceof Date ? d : new Date(typeof d === 'string' && !d.includes('T') ? d + 'T12:00:00' : d);
  if (isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
};

const ClashApprovalDialog = ({
  open,
  onClose,
  clashes = [],
  materialen = [],
  reservationsData = {},
  extendedStart,
  extendedEnd,
  currentReservationId,
  reservedMaterialIds = [],
  onConfirm,
}) => {
  // resolutions: { [materialId]: { mode: 'approve' } | { mode: 'replace', replacementId } }
  const [resolutions, setResolutions] = useState({});
  const [pickerOpenFor, setPickerOpenFor] = useState(null);
  const [search, setSearch] = useState('');

  // Reset bij heropenen
  useEffect(() => {
    if (open) { setResolutions({}); setPickerOpenFor(null); setSearch(''); }
  }, [open]);

  const extS = useMemo(() => (extendedStart ? new Date(extendedStart) : null), [extendedStart]);
  const extE = useMemo(() => (extendedEnd ? new Date(extendedEnd) : null), [extendedEnd]);

  // Is een materiaal vrij voor de verlengde periode? (geen overlappende andere reservering)
  const isFreeForPeriod = (matId) => {
    if (!extS || !extE) return true;
    const res = reservationsData[matId] || [];
    return !res.some(r =>
      r.reservationId !== currentReservationId &&
      new Date(r.startDate) <= extE && new Date(r.endDate) >= extS
    );
  };

  // Reeds gekozen vervangers (om dubbele toewijzing te voorkomen)
  const chosenReplacementIds = useMemo(
    () => new Set(Object.values(resolutions).filter(r => r.mode === 'replace' && r.replacementId).map(r => r.replacementId)),
    [resolutions]
  );

  const getReplacementOptions = (clashMaterial) => {
    const cat = clashMaterial?.fields?.Categorie;
    const clashIds = new Set(clashes.map(c => c.material.id));
    const term = search.trim().toLowerCase();
    return materialen.filter(m => {
      if (m.id === clashMaterial.id) return false;                 // niet zichzelf
      if (cat && m.fields?.Categorie !== cat) return false;        // zelfde categorie
      if (reservedMaterialIds.includes(m.id)) return false;        // niet al in de reservering
      if (clashIds.has(m.id)) return false;                        // geen ander clashend materiaal
      if (chosenReplacementIds.has(m.id)) return false;            // niet al elders gekozen
      if (!isFreeForPeriod(m.id)) return false;                    // vrij voor verlengde periode
      if (term && !(m.fields?.ID || '').toLowerCase().includes(term)) return false;
      return true;
    });
  };

  const isRowResolved = (matId) => {
    const r = resolutions[matId];
    if (!r) return false;
    return r.mode === 'approve' || (r.mode === 'replace' && !!r.replacementId);
  };

  const resolvedCount = clashes.filter(c => isRowResolved(c.material.id)).length;
  const allResolved = clashes.length > 0 && resolvedCount === clashes.length;

  const setApprove = (matId) => {
    setResolutions(prev => ({ ...prev, [matId]: { mode: 'approve' } }));
    if (pickerOpenFor === matId) setPickerOpenFor(null);
  };
  const openReplace = (matId) => {
    setResolutions(prev => ({ ...prev, [matId]: { mode: 'replace', replacementId: prev[matId]?.replacementId || null } }));
    setSearch('');
    setPickerOpenFor(pickerOpenFor === matId ? null : matId);
  };
  const chooseReplacement = (matId, replacementId) => {
    setResolutions(prev => ({ ...prev, [matId]: { mode: 'replace', replacementId } }));
    setPickerOpenFor(null);
  };
  const approveAll = () => {
    setResolutions(prev => {
      const next = { ...prev };
      clashes.forEach(c => { if (!isRowResolved(c.material.id)) next[c.material.id] = { mode: 'approve' }; });
      return next;
    });
    setPickerOpenFor(null);
  };

  const matLabel = (id) => materialen.find(m => m.id === id)?.fields?.ID || id;

  const choiceBtn = (active, color, icon, label, onClick) => (
    <Box onClick={onClick} sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.6, borderRadius: '0.5rem',
      cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
      fontFamily: T.fontBody, fontSize: '0.76rem', fontWeight: 700,
      border: `1.5px solid ${active ? color : T.outlineVariant + '50'}`,
      bgcolor: active ? color : T.surface,
      color: active ? '#fff' : T.secondary,
      '&:hover': { borderColor: color },
    }}>
      <Icon name={icon} size={15} sx={{ color: active ? '#fff' : color }} />
      {label}
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: '1rem', overflow: 'hidden' } }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: `1px solid ${T.surfaceHigh}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: '0.5rem', bgcolor: T.errorBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="event_busy" size={18} sx={{ color: T.error }} />
          </Box>
          <Typography sx={{ fontFamily: T.font, fontSize: '1.05rem', fontWeight: 800, color: T.text }}>
            Materiaal-conflict bij verlenging
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', color: T.secondary, lineHeight: 1.5 }}>
          Op de extra dag is dit materiaal al door een andere draaidag gereserveerd. Kies per materiaal: goedkeuren (dubbele boeking) of vervangen door een vrij materiaal.
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 1.5 }}>
        {/* Reminder */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, px: 1.5, py: 1.25, borderRadius: '0.5rem', bgcolor: T.amberBg, border: `1px solid ${T.amber}25` }}>
          <Icon name="groups" size={18} sx={{ color: T.amber, mt: 0.1, flexShrink: 0 }} />
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.text, lineHeight: 1.45 }}>
            Communiceer goedgekeurde dubbele boekingen altijd met het team van de betrokken draaidag(en).
          </Typography>
        </Box>

        {/* Teller + alles goedkeuren */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', fontWeight: 600, color: T.outline }}>
            {resolvedCount} van {clashes.length} afgehandeld
          </Typography>
          <Box onClick={approveAll} sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, borderRadius: '0.5rem',
            cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s',
            fontFamily: T.fontBody, fontSize: '0.74rem', fontWeight: 700, color: T.emerald,
            border: `1.5px solid ${T.emerald}40`, '&:hover': { bgcolor: T.emeraldBg },
          }}>
            <Icon name="done_all" size={15} sx={{ color: T.emerald }} />
            Alles goedkeuren
          </Box>
        </Box>

        {/* Per-materiaal rijen */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {clashes.map(({ material, conflicts }) => {
            const matId = material.id;
            const res = resolutions[matId];
            const resolved = isRowResolved(matId);
            const options = pickerOpenFor === matId ? getReplacementOptions(material) : [];
            return (
              <Box key={matId} sx={{
                borderRadius: '0.75rem', border: `1px solid ${resolved ? T.emerald + '50' : T.surfaceHigh}`,
                bgcolor: resolved ? T.emeraldBg : T.surfaceLow, overflow: 'hidden',
              }}>
                <Box sx={{ p: 1.5 }}>
                  {/* Materiaal + status */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontFamily: T.font, fontSize: '0.9rem', fontWeight: 800, color: T.text }}>
                          {material.fields?.ID || matId}
                        </Typography>
                        {material.fields?.Categorie && (
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.68rem', fontWeight: 600, color: T.outline, px: 0.75, py: 0.1, borderRadius: '0.35rem', bgcolor: T.surfaceHigh }}>
                            {material.fields.Categorie}
                          </Typography>
                        )}
                      </Box>
                      {/* Clashende draaidagen */}
                      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {conflicts.length > 0 ? conflicts.map((c, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Icon name="event" size={13} sx={{ color: T.error }} />
                            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary }}>
                              {c.draaidagNaam} <span style={{ color: T.outlineVariant }}>· {fmtDate(c.startDate)}{c.endDate && c.endDate !== c.startDate ? ` – ${fmtDate(c.endDate)}` : ''}</span>
                            </Typography>
                          </Box>
                        )) : (
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant }}>
                            Al gereserveerd door een andere draaidag.
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {resolved && <Icon name="check_circle" size={20} sx={{ color: T.emerald, flexShrink: 0 }} />}
                  </Box>

                  {/* Keuze + gekozen vervanger */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                    {choiceBtn(res?.mode === 'approve', T.emerald, 'check', 'Goedkeuren', () => setApprove(matId))}
                    {choiceBtn(res?.mode === 'replace', T.primary, 'swap_horiz', 'Vervangen', () => openReplace(matId))}
                    {res?.mode === 'replace' && res.replacementId && (
                      <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', fontWeight: 700, color: T.primary, ml: 0.5 }}>
                        → {matLabel(res.replacementId)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Vervang-picker */}
                {pickerOpenFor === matId && (
                  <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5, borderTop: `1px solid ${T.surfaceHigh}` }}>
                    <TextField
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder={`Zoek vrij ${material.fields?.Categorie || 'materiaal'}...`}
                      size="small" fullWidth autoFocus
                      sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '0.5rem', bgcolor: T.surface, fontFamily: T.fontBody, fontSize: '0.8rem' } }}
                    />
                    <Box sx={{ maxHeight: 180, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                      {options.length > 0 ? options.map(opt => (
                        <Box key={opt.id} onClick={() => chooseReplacement(matId, opt.id)} sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          px: 1.25, py: 0.75, borderRadius: '0.5rem', cursor: 'pointer', bgcolor: T.surface,
                          border: `1px solid ${T.surfaceHigh}`, transition: 'all 0.15s',
                          '&:hover': { borderColor: T.primary, bgcolor: T.primaryContainer },
                        }}>
                          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: 600, color: T.text }}>
                            {opt.fields?.ID || opt.id}
                          </Typography>
                          <Icon name="add" size={16} sx={{ color: T.primary }} />
                        </Box>
                      )) : (
                        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outlineVariant, px: 1, py: 1 }}>
                          Geen vrije materialen in deze categorie voor de verlengde periode.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, borderTop: `1px solid ${T.surfaceHigh}`, gap: 1 }}>
        <Box onClick={onClose} sx={{
          px: 2.5, py: 0.7, borderRadius: '0.5rem', cursor: 'pointer',
          fontFamily: T.fontBody, fontWeight: 600, fontSize: '0.78rem',
          color: T.secondary, border: `1.5px solid ${T.outlineVariant}40`,
          transition: 'all 0.15s', '&:hover': { bgcolor: T.surfaceLow },
        }}>
          Annuleren
        </Box>
        <Box
          onClick={allResolved ? () => onConfirm(resolutions) : undefined}
          sx={{
            px: 2.5, py: 0.7, borderRadius: '0.5rem',
            fontFamily: T.fontBody, fontWeight: 700, fontSize: '0.78rem',
            color: '#fff', bgcolor: allResolved ? T.emerald : T.outlineVariant,
            cursor: allResolved ? 'pointer' : 'not-allowed', opacity: allResolved ? 1 : 0.6,
            transition: 'all 0.15s', '&:hover': allResolved ? { filter: 'brightness(0.92)' } : {},
          }}>
          Doorgaan met verlenging
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ClashApprovalDialog;
